import React, { createContext, useContext, useEffect, useState } from 'react';
import { simpleAuth } from '../lib/simple-auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [themeData, setThemeData] = useState(null);
  const [itemsData, setItemsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameReady, setGameReady] = useState(false);
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(false);

  useEffect(() => {
    // Add listener for auth state changes from simple-auth
    const handleAuthChange = (newUser) => {
      // Don't update state if we're signing out (prevent UI flicker)
      if (!simpleAuth.isSigningOut) {
        setUser(newUser);
      }
    };

    simpleAuth.addListener(handleAuthChange);

    // Don't check initial session here - LoaderPage handles it
    // simpleAuth.getSession();

    return () => {
      simpleAuth.removeListener(handleAuthChange);
    };
  }, []);

  const signIn = async (callbackURL) => {
    await simpleAuth.signInWithGoogle(callbackURL);
  };

  const signOut = async () => {
    // Don't update local state - simple-auth will handle redirect
    await simpleAuth.signOut();
  };

  const updatePreferences = (newPreferences) => {
    setPreferences(newPreferences);
  };

  const setGameData = (userData, preferencesData, themeDataFromLoader = null, itemsDataFromLoader = null) => {
    setUser(userData);
    setPreferences(preferencesData);
    setThemeData(themeDataFromLoader);
    setItemsData(itemsDataFromLoader);
    setHasInitiallyFetched(true);
  };

  const markGameReady = (ready) => {
    setGameReady(ready);
  };

  const value = {
    user: gameReady ? user : null, // Only expose user when game is ready
    preferences,
    themeData,
    itemsData,
    loading,
    hasInitiallyFetched,
    signIn,
    signOut,
    updatePreferences,
    setGameData,
    setGameReady: markGameReady,
    isAuthenticated: gameReady ? !!user : false, // Only authenticate when game is ready
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};